import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProductAutocomplete from './ProductAutocomplete';
import CalculationModal from './CalculationModal';
import { normalizeNumericInput, toNumber } from '../utils/numericInput';

const INCREASE_OPTIONS = [0, 0.05, 0.1, 0.2, 0.3, 0.4];

/**
 * ClientQuoteItemRow
 * One row of the Client Quote items table. Total is intentionally simple:
 * unit_price * quantity * (1 + increase_percentage) — no area factor.
 * Area-based calculation and the Calculo button's real behavior are future
 * work (see docs/frontend/client_quote.md).
 */
const ClientQuoteItemRow = ({ item, index, products, onItemChange, onItemRemove, hiddenColumns = new Set(), readOnly = false }) => {
  const { t } = useTranslation();
  const [showCalculationModal, setShowCalculationModal] = useState(false);

  const calculateLineTotal = () => {
    const unitPrice = toNumber(item.unit_price);
    const quantity = toNumber(item.quantity);
    const increase = toNumber(item.increase_percentage);
    return unitPrice * quantity * (1 + increase);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  const calculationTypeLabel = (calculationType) => {
    if (calculationType === 'unidad') return t('clientQuotes.calculationTypeUnit');
    if (calculationType === 'm2') return t('clientQuotes.calculationTypeM2');
    if (calculationType === 'tira') return t('clientQuotes.calculationTypeStrip');
    return null;
  };

  // Stores the raw (normalised) string rather than a parsed number so the field
  // can be cleared and never accumulates a stale leading zero. Coercing an
  // empty field to 0 here is what made the zero impossible to delete.
  const handleNumericChange = (field) => (e) => {
    onItemChange(index, field, normalizeNumericInput(e.target.value));
  };

  const handleProductSelect = (product) => {
    onItemChange(index, 'productId', product.id);
    onItemChange(index, 'productName', product.name);
    onItemChange(index, 'description', product.name);
    onItemChange(index, 'finishing', product.description || '');
    onItemChange(index, 'unit_price', normalizeNumericInput(product.price ?? 0));
    onItemChange(index, 'measure', product.measure || { id: 1, name: 'Unitario', abbreviation: 'U' });
  };

  const handleCalculationApply = (calcData) => {
    Object.entries(calcData).forEach(([field, value]) => {
      if (value !== undefined) {
        onItemChange(index, field, value);
      }
    });
  };

  const selectedProduct = products.find((product) => product.id === item.productId);

  return (
    <tr>
      <td className="col-number col-sticky-left text-center">{index + 1}</td>

      <td className="col-product col-sticky-left">
        <ProductAutocomplete
          products={products}
          selectedProduct={item.productId ? { id: item.productId, name: item.productName } : null}
          onSelect={handleProductSelect}
          disabled={readOnly}
        />
      </td>

      <td className="col-calculation col-sticky-left text-center">
        <button
          type="button"
          className="btn btn-accent btn-small"
          disabled={readOnly || !item.productId}
          title={item.productId ? t('clientQuotes.calculation') : t('clientQuotes.selectProductFirst')}
          onClick={() => setShowCalculationModal(true)}
        >
          {t('clientQuotes.calculation')}
        </button>
        {item.calculation_type && (
          <div className="text-muted calculation-type-badge">
            {calculationTypeLabel(item.calculation_type)}
          </div>
        )}
        {(item.calculation_type === 'tira' || item.calculation_type === 'm2') && (
          <div className="text-muted calculation-type-badge">
            {t('clientQuotes.basePriceLabel')}: {formatCurrency(selectedProduct?.price)}
          </div>
        )}
        <CalculationModal
          isOpen={showCalculationModal}
          onClose={() => setShowCalculationModal(false)}
          onApply={handleCalculationApply}
          item={item}
          product={selectedProduct}
        />
      </td>

      {!hiddenColumns.has('description') && (
        <td>
          <textarea
            value={item.description || ''}
            onChange={(e) => onItemChange(index, 'description', e.target.value)}
            rows="3"
            className="cell-textarea"
            disabled={readOnly}
          />
        </td>
      )}

      <td className="text-center">
        <input
          type="text"
          value={item.area || ''}
          onChange={(e) => onItemChange(index, 'area', e.target.value)}
          placeholder={t('clientQuotes.areaPlaceholder')}
          className="cell-input"
          disabled={readOnly}
        />
      </td>

      {!hiddenColumns.has('finishing') && (
        <td>
          <textarea
            value={item.finishing || ''}
            onChange={(e) => onItemChange(index, 'finishing', e.target.value)}
            rows="3"
            className="cell-textarea"
            disabled={readOnly}
          />
        </td>
      )}

      {!hiddenColumns.has('quantity') && (
        <td className="text-center">
          <input
            type="text"
            inputMode="decimal"
            value={item.quantity ?? ''}
            onChange={handleNumericChange('quantity')}
            className="cell-input"
            aria-label={t('table.quantity')}
            disabled={readOnly}
          />
        </td>
      )}

      <td className="col-unit-price">
        <input
          type="text"
          inputMode="decimal"
          value={item.unit_price ?? ''}
          onChange={handleNumericChange('unit_price')}
          className="cell-input"
          aria-label={t('table.unitPrice')}
          disabled={readOnly}
        />
      </td>

      {!hiddenColumns.has('increasePercentage') && (
        <td className="col-increase">
          <select
            value={item.increase_percentage ?? 0}
            onChange={(e) => onItemChange(index, 'increase_percentage', toNumber(e.target.value))}
            className="cell-select"
            disabled={readOnly}
          >
            {INCREASE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {Math.round(option * 100)}%
              </option>
            ))}
          </select>
        </td>
      )}

      {!hiddenColumns.has('finalUnitPrice') && (
        <td className="col-final-price cell-total">
          {formatCurrency(item.final_price)}
        </td>
      )}

      <td className="col-total col-sticky-right cell-total">
        {formatCurrency(calculateLineTotal())}
      </td>

      <td className="col-actions col-sticky-right text-center">
        {!readOnly && (
          <button
            onClick={() => onItemRemove(index)}
            className="btn btn-danger btn-small"
          >
            {t('common.delete')}
          </button>
        )}
      </td>
    </tr>
  );
};

export default ClientQuoteItemRow;
